import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { ServiceRequestResponse } from '@/api/bol';
import {
  formatDateTime,
  formatLocation,
  getEntityDisplayName,
  formatExternalReferences,
  renderBooleanChip,
  renderChip,
} from '@/utils/datagridFormatters';

interface ServiceRequestColumnsProps {
  onDelete: (serviceRequest: ServiceRequestResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createServiceRequestColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: ServiceRequestColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'serviceName',
    headerName: 'Service Name',
    flex: 1.2,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { serviceName?: string }) => row.serviceName || '-',
  },
  {
    field: 'requestDateTime',
    headerName: 'Request Date',
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { requestDateTime?: string }) => formatDateTime(row.requestDateTime),
  },
  {
    field: 'requestStatus',
    headerName: 'Request Status',
    flex: 1,
    minWidth: 140,
    renderCell: (params) => renderChip('requestStatus', params.row.requestStatus),
  },
  {
    field: 'responseDateTime',
    headerName: 'Response Date',
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { responseDateTime?: string }) => formatDateTime(row.responseDateTime),
  },
  {
    field: 'responseStatus',
    headerName: 'Response Status',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { responseStatus?: string }) => row.responseStatus || '-',
  },
  {
    field: 'serviceType',
    headerName: 'Service Type',
    flex: 1,
    minWidth: 130,
    renderCell: (params) => renderChip('serviceType', params.row.serviceType),
  },
  {
    field: 'serviceSubtype',
    headerName: 'Service Subtype',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => renderChip('serviceSubtype', params.row.serviceSubtype),
  },
  {
    field: 'requestor',
    headerName: 'Requestor',
    flex: 1.1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { requestor?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } }) =>
      getEntityDisplayName(row.requestor),
  },
  {
    field: 'provider',
    headerName: 'Provider',
    flex: 1.1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { provider?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } }) =>
      getEntityDisplayName(row.provider),
  },
  {
    field: 'startLocation',
    headerName: 'Start',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { startLocation?: unknown }) => formatLocation(row.startLocation as {
      location?: {
        address?: {
          addressLocality?: string | null;
          postalCode?: string | null;
        } | null;
      } | null;
    }),
  },
  {
    field: 'endLocation',
    headerName: 'End',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { endLocation?: unknown }) => formatLocation(row.endLocation as {
      location?: {
        address?: {
          addressLocality?: string | null;
          postalCode?: string | null;
        } | null;
      } | null;
    }),
  },
  {
    field: 'isDangerous',
    headerName: 'Dangerous',
    flex: 0.8,
    minWidth: 110,
    renderCell: (params) => renderBooleanChip(params.row.isDangerous),
  },
  {
    field: 'requestedActivitiesCount',
    headerName: 'Activities',
    flex: 0.8,
    minWidth: 110,
    valueGetter: (_value: unknown, row: { requestedActivities?: unknown[] }) => row.requestedActivities?.length || 0,
  },
  {
    field: 'consignmentsCount',
    headerName: 'Consignments',
    flex: 0.8,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { consignments?: unknown[] }) => row.consignments?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/service-requests/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as ServiceRequestResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultServiceRequestColumnVisibilityModel = {
  id: true,
  serviceName: true,
  requestDateTime: true,
  requestStatus: true,
  responseDateTime: true,
  responseStatus: true,
  serviceType: true,
  serviceSubtype: true,
  requestor: true,
  provider: false,
  startLocation: true,
  endLocation: true,
  isDangerous: false,
  requestedActivitiesCount: false,
  consignmentsCount: false,
  externalReferences: false,
};
